export const convertPatternToNaturalLanguage = (
  pattern: string,
  t: (key: string, options?: any) => string
) => {
  const parts = pattern.split(' ')

  const days = [
    t('Sunday'),
    t('Monday'),
    t('Tuesday'),
    t('Wednesday'),
    t('Thursday'),
    t('Friday'),
    t('Saturday')
  ]
  const months = [
    t('January'),
    t('February'),
    t('March'),
    t('April'),
    t('May'),
    t('June'),
    t('July'),
    t('August'),
    t('September'),
    t('October'),
    t('November'),
    t('December')
  ]

  const getOrdinal = (n: number) => {
    const s = [t('th'), t('st'), t('nd'), t('rd')]
    const v = n % 100
    return n + (s[(v - 20) % 10] || s[v] || s[0])
  }

  // Parse time components
  const getTimeString = (hour: string, minute: string) => {
    if (hour === '*' && minute === '*') return t('any time')
    if (hour === '*' && minute !== '*')
      return t('every hour at {{minutes}} minutes', { minutes: parseInt(minute) })
    if (minute === '*' && hour !== '*') return t('every minute of {{hour}}:00', { hour })

    const h = parseInt(hour)
    const m = parseInt(minute)
    const period = h >= 12 ? t('PM') : t('AM')
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    return `${hour12}${m > 0 ? ':' + m.toString().padStart(2, '0') : ''} ${period}`
  }

  if (parts.length === 5) {
    const [minute, hour, dayMonth, month, dayWeek] = parts

    // Handle common patterns
    if (minute === '*' && hour === '*' && dayMonth === '*' && month === '*' && dayWeek === '*') {
      return 'Every minute'
    }
    if (minute === '0' && hour === '*' && dayMonth === '*' && month === '*' && dayWeek === '*') {
      return 'Every hour'
    }
    if (minute === '0' && hour === '0' && dayMonth === '*' && month === '*' && dayWeek === '*') {
      return 'Every day at midnight'
    }

    // Handle intervals
    if (minute.includes('/')) {
      const interval = parseInt(minute.split('/')[1])
      return interval === 1 ? 'Every minute' : `Every ${interval} minutes`
    }
    if (hour.includes('/')) {
      const interval = parseInt(hour.split('/')[1])
      return interval === 1 ? 'Every hour' : `Every ${interval} hours`
    }

    // Handle specific times
    const timeStr = getTimeString(hour, minute)

    // Handle days of week
    if (dayWeek !== '*') {
      const weekDays = dayWeek.split(',').map((d) => {
        const num = parseInt(d)
        return days[num === 7 ? 0 : num] // Handle both 0 and 7 for Sunday
      })

      if (weekDays.length > 1) {
        return `${timeStr} on ${weekDays.slice(0, -1).join(', ')} and ${weekDays.slice(-1)}`
      }
      return `${timeStr} on ${weekDays[0]}`
    }

    // Handle specific dates
    if (dayMonth !== '*') {
      if (month !== '*') {
        const monthNames = month.split(',').map((m) => months[parseInt(m) - 1])
        const dates = dayMonth.split(',').map((d) => getOrdinal(parseInt(d)))

        if (monthNames.length > 1) {
          return `${timeStr} on the ${dates.join(' and ')} of ${monthNames.join(' and ')}`
        }
        if (dates.length > 1) {
          return `${timeStr} on the ${dates.join(' and ')} of ${monthNames[0]}`
        }
        return `${timeStr} on ${monthNames[0]} ${dates[0]}`
      }

      const dates = dayMonth.split(',').map((d) => getOrdinal(parseInt(d)))
      if (dates.length > 1) {
        return `${timeStr} on the ${dates.join(' and ')} of every month`
      }
      return `${timeStr} on the ${dates[0]} of every month`
    }

    // Handle every day at specific time
    if (hour !== '*' || minute !== '*') {
      if (hour === '0' && minute === '0') {
        return 'Daily at midnight'
      }
      if (hour === '12' && minute === '0') {
        return 'Daily at noon'
      }
      return `Daily at ${timeStr}`
    }
  }

  return pattern // Return original if no match
}
